import { gql, useMutation, useQuery } from '@apollo/client'
import { useEffect, useMemo, useState } from 'react'

import {
  ArtistMembershipPurchaseConfirmationQuery,
  ArtistMembershipPurchaseConfirmationQueryVariables,
  ArtistMembershipSubscriptionDetailFragmentDoc,
  ArtistWithPatronFragmentDoc,
  PaymentDetailFragmentDoc,
  PaymentMethod,
  StartPendinggSubscriptionMutation,
  StartPendinggSubscriptionMutationVariables,
  StartSubscriptionProcessMutation,
  StartSubscriptionProcessMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config, constants } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { getActiveMembershipSubscriptionProductPlan } from '@/core/membership'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'
import {
  getPrefrenceObject,
  removePrefernce,
  setRegisterNewMembership,
} from '@/core/native/storage'
import {
  CARRIER_PAYMENT_METHODS,
  isCarrierPaymentMethod,
  submitEscottForm,
  useBFCacheRestore,
} from '@/lib/escott'

import { ArtistMembershipPurchaseInput } from '../ArtistMembershipPurchaseProceed/template/ArtistMembershipPurchaseForm'
import { Action, State } from './template'

// 推しメンバー情報の型
type FavoriteMemberInfo = {
  id: string
  name: string
  avatar: string
}

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('artistMembershipPurchaseConfirmation')
  const { router, generatePath, routerPages } =
    useRouter<{ artistId: string; productId: string }>()
  const { showError } = useMessage()
  const { showModal } = useSettingUserInfoModal()

  const [initialPlan, setInitialPlan] =
    useState<ArtistMembershipPurchaseInput>()

  const artistId =
    (config.applicationType === 'standalone'
      ? config.artistId
      : router.query.artistId) ?? '#'

  const { data, loading } = useQuery<
    ArtistMembershipPurchaseConfirmationQuery,
    ArtistMembershipPurchaseConfirmationQueryVariables
  >(QUERY, { variables: { id: artistId } })

  // 外部キャリア決済画面（escott）へのリダイレクト中かどうかを管理するフラグ
  // mutation の完了から escott フォーム送信までに
  // submit ボタンが活性化してしまうのを防ぐために用いている
  const [isRedirectingToEscott, setIsRedirectingToEscott] = useState(false)

  const [startSubscriptionProcess, { loading: startingSubscriptionProcess }] =
    useMutation<
      StartSubscriptionProcessMutation,
      StartSubscriptionProcessMutationVariables
    >(START_SUBSCRIPTION_PROCESS, {
      async onCompleted({ startSubscriptionProcess }) {
        switch (startSubscriptionProcess.__typename) {
          // キャリア決済を想定
          case 'EscottMasterResult':
            // 外部のキャリア決済画面へフォーム送信によってリダイレクト
            // 後続処理が外部画面とバックエンドで実行された後に
            // ステータスに応じて escottInput で指定した画面に復帰する
            submitEscottForm(
              startSubscriptionProcess.nextActionUrl,
              startSubscriptionProcess.parameter
            )
            break
          // クレジットカード払いを想定
          case 'Patron':
            await removePrefernce('artistMembershipPurchaseInput')
            await setRegisterNewMembership()
            // 推しメンバー情報を削除
            removeFavoriteMemberInfo()
            if (startSubscriptionProcess?.isPersonalInformationRequired) {
              router.replace(generatePath(routerPages.ArtistTop, { artistId }))
              return showModal({
                showSnackbarWhenClose: true,
                isRegisterNewMembership: true,
              })
            }
            return router.replace(
              generatePath(routerPages.ArtistMembershipCompletion, {
                artistId,
                productId: router.query.productId,
              })
            )
          default:
            break
        }
      },
    })

  const [startPendingSubscription, { loading: startingPendingSubscription }] =
    useMutation<
      StartPendinggSubscriptionMutation,
      StartPendinggSubscriptionMutationVariables
    >(START_PENDING_SUBSCRIPTION, {
      async onCompleted({ startPendinggSubscription }) {
        await removePrefernce('artistMembershipPurchaseInput')
        await setRegisterNewMembership()
        // 推しメンバー情報を削除
        removeFavoriteMemberInfo()
        let pendingPurchaseId = startPendinggSubscription.id
        if ('artist' in startPendinggSubscription) {
          pendingPurchaseId =
            startPendinggSubscription.artist?.pendingPurchasePatron?.id ?? '#'
        }
        return router.replace(
          generatePath(routerPages.ArtistMembershipKonbiniCompletion, {
            artistId,
            productId: router.query.productId,
            pendingPurchaseId,
          })
        )
      },
    })

  const product = useMemo(() => {
    const activeProduct = getActiveMembershipSubscriptionProductPlan(
      data?.artist,
      initialPlan?.planId
    )
    if (activeProduct?.productId !== router.query.productId) {
      return
    }
    return activeProduct
  }, [data?.artist, initialPlan?.planId, router.query.productId])

  useEffect(() => {
    ;(async () => {
      const v = await getPrefrenceObject<ArtistMembershipPurchaseInput>(
        'artistMembershipPurchaseInput'
      )
      setInitialPlan({
        interval: v.interval,
        paymentMethod: v.paymentMethod,
        planId: v.planId,
      })
    })()
  }, [])

  // 外部キャリア決済画面（escott）からブラウザバックした際
  // リダイレクト中かどうかを管理するフラグをリセットする
  // ブラウザバック時に BFCache が効き再読み込みが走らない環境で
  // ローディング状態が解除されないことを防ぐ
  useBFCacheRestore(() => {
    setIsRedirectingToEscott(false)
  })

  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  // 推しメンバー情報を取得
  const favoriteMemberInfo = useMemo(() => {
    try {
      const storedData = localStorage.getItem(
        'artistMembershipFavoriteMembersSelect'
      )
      if (storedData) {
        const parsedData = JSON.parse(storedData) as FavoriteMemberInfo
        return parsedData
      }
    } catch (error) {
      showError(error as Error)
    }
    return undefined
  }, [artistId])

  // 推しメンバー情報を削除する関数
  const removeFavoriteMemberInfo = () => {
    try {
      localStorage.removeItem('artistMembershipFavoriteMembersSelect')
    } catch (error) {
      showError(error as Error)
    }
  }

  return {
    state: {
      loading,
      artistId: artistId,
      product,
      paymentMethod: initialPlan?.paymentMethod,
      paymentSource: data?.user?.credit ?? null,
      favoriteMemberInfo,
      isSubmitting:
        initialPlan?.paymentMethod !== undefined &&
        [...CARRIER_PAYMENT_METHODS, PaymentMethod.CreditCard].includes(
          initialPlan?.paymentMethod
        )
          ? startingSubscriptionProcess || isRedirectingToEscott
          : startingPendingSubscription,
      openExpiredCardModal,
      openPaymentErrorModal,
      openRequire3DSecureModal,
      joined: !!data?.artist?.patron,
    },
    actions: {
      async onSubmit() {
        if (!initialPlan) {
          return
        }
        // クレジットカード決済の場合
        if (initialPlan.paymentMethod === PaymentMethod.CreditCard) {
          await startSubscriptionProcess({
            variables: {
              artistId,
              planId: initialPlan.planId,
              options: favoriteMemberInfo
                ? {
                    favoriteMemberId: favoriteMemberInfo.id,
                  }
                : undefined,
            },
          }).catch(error => {
            switch (error.graphQLErrors?.[0]?.extensions?.errorCode) {
              /**
               * @description
               * 3Dセキュア認証が必要な場合
               */
              case constants.errorCodes.REQUIRE_3D_SECURE:
                setOpenRequire3DSecureModal(true)
                return
              /**
               * @description
               * カードが有効期限切れの場合
               */
              case constants.errorCodes.EXPIRED_CARD:
                setOpenExpiredCardModal(true)
                return
              /**
               * @description
               * 何かしらの決済エラーの場合
               */
              case constants.errorCodes.PAYMENT_ERROR:
                setOpenPaymentErrorModal(true)
                return
              default:
                showError(error)
                break
            }
          })
          return
        }
        // キャリア決済の場合
        if (isCarrierPaymentMethod(initialPlan.paymentMethod)) {
          setIsRedirectingToEscott(true)
          await startSubscriptionProcess({
            variables: {
              artistId,
              planId: initialPlan.planId,
              paymentMethod: initialPlan.paymentMethod,
              options: favoriteMemberInfo
                ? {
                    favoriteMemberId: favoriteMemberInfo.id,
                  }
                : undefined,
              escottInput: {
                // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
                successUrl: `${window.location.origin}${generatePath(
                  routerPages.ArtistMembershipCompletion,
                  {
                    artistId,
                    productId: router.query.productId,
                  }
                )}`,
                // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
                cancelUrl: `${window.location.origin}${generatePath(
                  routerPages.ArtistMembershipPurchaseConfirmation,
                  {
                    artistId,
                    productId: router.query.productId,
                  }
                )}`,
                // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
                errorUrl: `${window.location.origin}${generatePath(
                  routerPages.ArtistMembershipPurchaseConfirmation,
                  {
                    artistId,
                    productId: router.query.productId,
                  }
                )}`,
              },
            },
          }).catch(error => {
            switch (error.graphQLErrors?.[0]?.extensions?.errorCode) {
              // メンバーシップが閉じられていた場合
              case constants.errorCodes.SUBSCRIPTION_PLAN_NOT_ACTIVE:
                showError(t('subscriptionPlanNotActive'))
                break
              default:
                showError(error)
                break
            }
            setIsRedirectingToEscott(false)
          })
          return
        }
        // コンビニ払いの場合
        await startPendingSubscription({
          variables: {
            artistId,
            planId: initialPlan.planId,
            options: favoriteMemberInfo
              ? {
                  favoriteMemberId: favoriteMemberInfo.id,
                }
              : undefined,
          },
        }).catch(showError)
      },
      onCloseRequire3DSecureModal() {
        setOpenRequire3DSecureModal(false)
      },
      onCloseExpiredCardModal() {
        setOpenExpiredCardModal(false)
      },
      onClosePaymentErrorModal() {
        setOpenPaymentErrorModal(false)
      },
    },
  }
})

const QUERY = gql`
  query ArtistMembershipPurchaseConfirmation($id: ID) {
    artist(id: $id) {
      id
      ...ArtistMembershipSubscriptionDetail
      ...ArtistWithPatron
    }
    user {
      id
      credit {
        ...PaymentDetail
      }
    }
  }
  ${ArtistMembershipSubscriptionDetailFragmentDoc}
  ${ArtistWithPatronFragmentDoc}
  ${PaymentDetailFragmentDoc}
`

const START_SUBSCRIPTION_PROCESS = gql`
  mutation StartSubscriptionProcess(
    $artistId: ID!
    $planId: ID!
    $paymentMethod: PaymentMethod
    $escottInput: EscottMasterInput
    $options: StartSubscriptionOptions
  ) {
    startSubscriptionProcess(
      artistId: $artistId
      planId: $planId
      paymentMethod: $paymentMethod
      escottInput: $escottInput
      options: $options
    ) {
      __typename
      ... on PendingPurchaseItem {
        id
      }
      ... on Patron {
        id
        isPersonalInformationRequired
      }
      ... on EscottMasterResult {
        nextActionUrl
        parameter {
          encryptValue
          merchantId
          openID
          operateId
        }
      }
    }
  }
`

const START_PENDING_SUBSCRIPTION = gql`
  mutation StartPendinggSubscription(
    $artistId: ID!
    $planId: String!
    $options: StartSubscriptionOptions
  ) {
    startPendinggSubscription(
      artistId: $artistId
      planId: $planId
      options: $options
    ) {
      ... on PendingPurchaseItem {
        id
      }
      ... on Patron {
        id
        artist {
          id
          pendingPurchasePatron {
            id
          }
        }
      }
    }
  }
`
